// ================================================================
// ⚡ useGlobalPerformanceGuard — نسخه‌ی نهایی v1.0
// ================================================================
// یک هوک سراسری برای کل اپ:
//   ۱. تب مخفی → همه‌ی انیمیشن‌ها pause
//   ۲. عنصر خارج از ویوئو → انیمیشن‌هاش pause
//   ۳. برگشت به دید → دقیقاً از همون‌جا ادامه
//
// ✅ مستقل از محل تعریف CSS
// ✅ کار می‌کنه با کلاس‌های ایزوله
// ✅ پوشش خودکار صفحات و کارت‌های آینده
// ✅ صفر تغییر بصری
// ================================================================

import { useEffect } from 'react';

const SAFE_SKIP_TAGS = new Set([
  'HTML', 'HEAD', 'BODY', 'SCRIPT', 'STYLE', 'LINK', 'META', 'TITLE',
]);

export function useGlobalPerformanceGuard(options = {}) {
  const {
    rootMargin = '150px 0px',   // حاشیه‌ی پیش‌بارگذاری (px)
    idleTimeout = 250,           // حداکثر انتظار برای idle
    debug = false,
  } = options;

  useEffect(() => {
    const html = document.documentElement;
    const log = debug
      ? (...args) => console.log('%c[perf-guard]', 'color:#8b5cf6;font-weight:bold', ...args)
      : () => {};

    // ============================================================
    // ۱) Tab Visibility — وقتی تب مخفی شد، همه پاز
    // ============================================================
    const onVisibility = () => {
      const hidden = document.hidden;
      html.classList.toggle('perf-tab-hidden', hidden);
      log('tab', hidden ? 'hidden → all paused' : 'visible → resumed');
    };
    document.addEventListener('visibilitychange', onVisibility);
    onVisibility();

    // ============================================================
    // ۲) IntersectionObserver — عناصر خارج از دید
    // ============================================================
    const hasIO = typeof IntersectionObserver !== 'undefined';
    let io = null;

    if (hasIO) {
      io = new IntersectionObserver(
        (entries) => {
          // Batch update — یکبار برای همه‌ی عناصر این فریم
          for (let i = 0; i < entries.length; i++) {
            const e = entries[i];
            e.target.classList.toggle('perf-offscreen', !e.isIntersecting);
          }
        },
        { rootMargin, threshold: 0 }
      );
    } else {
      log('IntersectionObserver not supported — tab-pause only');
    }

    // ============================================================
    // ۳) Tracking sets
    // ============================================================
    const observed = new WeakSet();
    const pendingRoots = new Set();

    // ============================================================
    // ۴) Scanner — پیدا کردن عناصر انیمیت‌دار با getAnimations
    // ============================================================
    const hasGetAnimations =
      typeof Element.prototype.getAnimations === 'function';

    const scanRoot = (root) => {
      if (!root || root.nodeType !== 1) return;
      if (SAFE_SKIP_TAGS.has(root.tagName)) return;
      if (!root.isConnected) return;

      let animations = [];
      try {
        animations = hasGetAnimations
          ? root.getAnimations({ subtree: true })
          : [];
      } catch {
        return;
      }

      if (animations.length === 0) return;

      // جمع‌آوری عناصر یکتا (effect.target به عنصر والد pseudo-element برمی‌گرده)
      const targets = new Set();
      for (const anim of animations) {
        const t = anim.effect?.target;
        if (t && t.nodeType === 1 && t.isConnected) targets.add(t);
      }

      let added = 0;
      for (const t of targets) {
        if (observed.has(t)) continue;
        if (SAFE_SKIP_TAGS.has(t.tagName)) continue;
        observed.add(t);
        if (io) io.observe(t);
        added++;
      }
      if (added > 0) log('observed', added, 'new animated elements');
    };

    // ============================================================
    // ۵) Idle scheduler — هیچ‌وقت روی main thread block نمی‌کنیم
    // ============================================================
    const scheduleIdle = (fn) => {
      if (typeof window.requestIdleCallback === 'function') {
        return window.requestIdleCallback(fn, { timeout: idleTimeout });
      }
      return window.setTimeout(fn, idleTimeout);
    };
    const cancelIdle = (id) => {
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(id);
      } else {
        clearTimeout(id);
      }
    };

    // ============================================================
    // ۶) Batched flush
    // ============================================================
    let flushScheduled = false;
    let flushIdleId = null;

    const flush = () => {
      flushScheduled = false;
      flushIdleId = null;
      const roots = Array.from(pendingRoots);
      pendingRoots.clear();
      for (const r of roots) scanRoot(r);
    };

    const scheduleFlush = () => {
      if (flushScheduled) return;
      flushScheduled = true;
      flushIdleId = scheduleIdle(flush);
    };

    // ============================================================
    // ۷) MutationObserver — عناصری که بعداً اضافه می‌شن
    // ============================================================
    const mo = new MutationObserver((mutations) => {
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        if (m.type === 'childList') {
          const added = m.addedNodes;
          for (let j = 0; j < added.length; j++) {
            const n = added[j];
            if (n.nodeType === 1) pendingRoots.add(n);
          }
        } else if (m.type === 'attributes') {
          // خودمون کلاس perf-offscreen رو اضافه/حذف می‌کنیم — نادیده بگیر
          if (observed.has(m.target)) continue;
          pendingRoots.add(m.target);
        }
      }
      if (pendingRoots.size > 0) scheduleFlush();
    });

    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    // ============================================================
    // ۸) اسکن اولیه — روی idle
    // ============================================================
    const initialScanId = scheduleIdle(() => {
      log('initial full scan started');
      scanRoot(document.body);
      log('initial full scan done');
    });

    // ============================================================
    // ۹) Re-scan on focus (وقتی کاربر از تب دیگه برمی‌گرده)
    // ============================================================
    const onFocus = () => {
      if (!document.hidden) {
        scheduleIdle(() => scanRoot(document.body));
      }
    };
    window.addEventListener('focus', onFocus, { passive: true });

    // ============================================================
    // ۱۰) bfcache restore (Safari/Chrome back-forward cache)
    // ============================================================
    const onPageShow = (e) => {
      if (e.persisted) {
        scheduleIdle(() => scanRoot(document.body));
      }
    };
    window.addEventListener('pageshow', onPageShow, { passive: true });

    // ============================================================
    // Cleanup
    // ============================================================
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      mo.disconnect();
      if (io) io.disconnect();
      cancelIdle(initialScanId);
      if (flushIdleId != null) cancelIdle(flushIdleId);
      html.classList.remove('perf-tab-hidden');
      log('cleanup complete');
    };
  }, [rootMargin, idleTimeout, debug]);
}